import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaBars, FaTimes, FaTelegram } from 'react-icons/fa';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 10;
      setScrolled(isScrolled);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { name: 'Home', path: '/' },
    { name: 'About', path: '/about' },
    { name: 'Features', path: '/features' },
    { name: 'Documentation', path: '/docs' },
    { name: 'Contact', path: '/contact' },
  ];

  return (
    <nav className={`fixed w-full z-50 transition-all duration-300 ${
      scrolled ? 'bg-primary-50/95 backdrop-blur-md shadow-lg border-b border-accent-300' : 'bg-transparent'
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3">
            <img 
              src="/images/boulevard logo.png" 
              alt="RideEase Logo" 
              className="w-10 h-10 object-contain"
            />
            <span className="text-2xl font-display font-bold text-primary-800">
              Boulevard
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {navItems.map((item) => (
              <Link
                key={item.name}
                to={item.path}
                className={`text-sm font-medium transition-colors duration-200 ${
                  location.pathname === item.path
                    ? 'text-accent-500 border-b-2 border-accent-400'
                    : scrolled
                    ? 'text-primary-700 hover:text-accent-600'
                    : 'text-white hover:text-accent-300'
                }`}
              >
                {item.name}
              </Link>
            ))}
            
            <div className="flex items-center space-x-4">
              <Link
                to="/signup"
                className={`text-sm font-medium transition-colors duration-200 ${
                  location.pathname === '/signup'
                    ? 'text-accent-500 border-b-2 border-accent-400'
                    : scrolled
                    ? 'text-primary-700 hover:text-accent-600'
                    : 'text-white hover:text-accent-300'
                }`}
              >
                Sign Up
              </Link>
              <div className="flex space-x-2">
                <a
                  href="https://t.me/Rideatease_bot"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded-lg transition-all duration-200 flex items-center space-x-1 shadow-md hover:shadow-lg text-sm"
                >
                  <FaTelegram />
                  <span>Rider</span>
                </a>
                <a
                  href="https://t.me/Rideeasedriver_bot"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-green-600 hover:bg-green-700 text-white font-semibold py-2 px-4 rounded-lg transition-all duration-200 flex items-center space-x-1 shadow-md hover:shadow-lg text-sm"
                >
                  <FaTelegram />
                  <span>Driver</span>
                </a>
              </div>
            </div>
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className={`p-2 rounded-md ${
                scrolled ? 'text-primary-700 hover:text-accent-500' : 'text-white hover:text-accent-300'
              }`}
            >
              {isOpen ? <FaTimes size={24} /> : <FaBars size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="md:hidden bg-primary-50 border border-accent-200 rounded-lg shadow-lg m-2 p-4"
          >
            {navItems.map((item) => (
              <Link
                key={item.name}
                to={item.path}
                className={`block py-2 text-base font-medium ${
                  location.pathname === item.path
                    ? 'text-accent-600 border-l-2 border-accent-500 pl-2'
                    : 'text-primary-700 hover:text-accent-600'
                }`}
                onClick={() => setIsOpen(false)}
              >
                {item.name}
              </Link>
            ))}
            
            <Link
              to="/signup"
              className={`block py-2 text-base font-medium ${
                location.pathname === '/signup'
                  ? 'text-accent-600 border-l-2 border-accent-500 pl-2'
                  : 'text-primary-700 hover:text-accent-600'
              }`}
              onClick={() => setIsOpen(false)}
            >
              Sign Up
            </Link>
            
            <div className="flex flex-col space-y-2 mt-4">
              <a
                href="https://t.me/Rideatease_bot"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg transition-all duration-200 w-full flex items-center justify-center space-x-2 shadow-md"
              >
                <FaTelegram />
                <span>Rider Bot</span>
              </a>
              <a
                href="https://t.me/Rideeasedriver_bot"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-green-600 hover:bg-green-700 text-white font-semibold py-3 px-6 rounded-lg transition-all duration-200 w-full flex items-center justify-center space-x-2 shadow-md"
              >
                <FaTelegram />
                <span>Driver Bot</span>
              </a>
            </div>
          </motion.div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
